import React, { useEffect, useState } from 'react';
import {
  useLazyGetMostRecentPoliciesQuery,
  useUpdateSettingsMutation,
} from 'services/settingsService';
import { PrivacyPolicy } from 'components/PrivacyPolicy/PrivacyPolicy';
import { Button } from 'components/Button/Button';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { Grid2, styled } from '@mui/material';
import { ErrorDialog } from 'components/ErrorDialog/ErrorDialog';

const DesktopTabletStyledGrid = styled(Grid2)(({ theme }) => ({
  overflow: 'auto',
  width: '100%',
  flex: 1,
}));

const DesktopTabletStyledGridItem = styled(Grid2)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '580px',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  margin: 'auto',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

interface PrivacyPolicyReconsentProps {
  onReconsent: () => void;
}

export const PrivacyPolicyReconsent = ({ onReconsent }: PrivacyPolicyReconsentProps) => {
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [eulaPolicyAccepted, setEulaPolicyAccepted] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const { translation } = useTranslationContext();
  const user = useLoggedInUser();
  const [
    getMostRecentPolicies,
    { isFetching: isFetchingLatestPolicies, data: latestPoliciesData },
  ] = useLazyGetMostRecentPoliciesQuery();
  const [updateSettings, { isLoading: isUpdatingSettings }] = useUpdateSettingsMutation();

  useEffect(() => {
    if (user?.code) {
      getMostRecentPolicies(user?.code);
    }
  }, [getMostRecentPolicies, user?.code]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (latestPoliciesData?.privacy_policy?.effective_date) {
      const { data, error } = await updateSettings({
        settings: {
          privacy_policy: {
            effective_date: latestPoliciesData.privacy_policy.effective_date,
          },
        },
      });
      if (error) {
        setShowErrorDialog(true);
        return;
      }
      onReconsent();
    }
  };

  if (latestPoliciesData?.eula_policy && latestPoliciesData?.privacy_policy) {
    return (
      <>
        <DesktopTabletStyledGrid direction="column" container>
          <DesktopTabletStyledGridItem>
            <form onSubmit={handleSubmit} noValidate>
              <PrivacyPolicy
                title={
                  'We have made updates to our End User License Agreement (EULA) and Privacy Policy.' +
                  'Tap the button below to review and accept these agreements.'
                }
                privacyPolicyUrl={latestPoliciesData.privacy_policy.url}
                eulaPolicyUrl={latestPoliciesData.eula_policy.url}
                privacyPolicyAcceptanceStateFieldProps={{
                  checked: privacyPolicyAccepted,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setPrivacyPolicyAccepted(e.target.checked);
                  },
                }}
                eulaPolicyAcceptanceStateFieldProps={{
                  checked: eulaPolicyAccepted,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setEulaPolicyAccepted(e.target.checked);
                  },
                }}
              />
              <StyledButton
                variant="primary"
                type="submit"
                size="xlarge"
                disabled={
                  !privacyPolicyAccepted ||
                  !eulaPolicyAccepted ||
                  isUpdatingSettings ||
                  isFetchingLatestPolicies
                }
                isLoading={isUpdatingSettings}
                fullWidth
              >
                {translation('accept_all')}
              </StyledButton>
            </form>
          </DesktopTabletStyledGridItem>
        </DesktopTabletStyledGrid>
        <ErrorDialog
          open={showErrorDialog}
          title={translation('something_has_gone_wrong')}
          body=""
          primaryCtaButton={{
            label: translation('try_again'),
            onClick: () => {
              setShowErrorDialog(false);
            },
          }}
          onClose={() => {
            setShowErrorDialog(false);
          }}
        />
      </>
    );
  }

  return null;
};
